/**
 * Carrier Cutoff Form Modal
 * Edit a carrier's daily schedule times (all times Asia/Baghdad, HH:mm)
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import type { MergedCarrierCutoff, SetCarrierCutoffInput } from '../../services/carrierCutoffs.api';
import { hhmmToOperationalMinutes } from '../../utils/carrierCutoffStatus';

interface CutoffFormModalProps {
  carrier: MergedCarrierCutoff;
  onSave: (carrierId: number, input: SetCarrierCutoffInput) => void;
  onClose: () => void;
  isSaving: boolean;
  error?: string | null;
}

export default function CutoffFormModal({
  carrier,
  onSave,
  onClose,
  isSaving,
  error,
}: CutoffFormModalProps) {
  const [returnsArrivalTime, setReturnsArrivalTime] = useState(
    carrier.cutoff?.returns_arrival_time || ''
  );
  const [collectionStartTime, setCollectionStartTime] = useState(
    carrier.cutoff?.collection_start_time || ''
  );
  const [cutoffTime, setCutoffTime] = useState(carrier.cutoff?.cutoff_time || '');
  const [notes, setNotes] = useState(carrier.cutoff?.notes || '');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!cutoffTime) {
      setValidationError('Final cutoff time is required');
      return;
    }
    if (
      collectionStartTime &&
      hhmmToOperationalMinutes(collectionStartTime) >= hhmmToOperationalMinutes(cutoffTime)
    ) {
      setValidationError(
        'Collection start must be before the final cutoff (within the 04:00-04:00 operational day)'
      );
      return;
    }

    onSave(carrier.carrier_id, {
      returnsArrivalTime: returnsArrivalTime || null,
      collectionStartTime: collectionStartTime || null,
      cutoffTime,
      notes: notes || undefined,
      isActive: true,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{carrier.carrier_title}</h2>
            <p className="text-xs text-gray-500">
              {carrier.carrier_slug} · times in Baghdad (Asia/Baghdad)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Returns arrival time <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="time"
              value={returnsArrivalTime}
              onChange={(e) => setReturnsArrivalTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">When the carrier delivers returned shipments</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Collection start <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="time"
              value={collectionStartTime}
              onChange={(e) => setCollectionStartTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Earliest outgoing-mail collection</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Final cutoff *</label>
            <input
              type="time"
              value={cutoffTime}
              onChange={(e) => setCutoffTime(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Last time the carrier collects outgoing mail. Times before 04:00 count as the
              end of the same operational day (overnight carriers, e.g. cutoff 03:30).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Counter vehicle arrives before 14:00"
            />
          </div>

          {(validationError || error) && (
            <p className="text-sm text-red-600">{validationError || error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
